import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  TablePagination,
} from '@mui/material';
import { Add as AddIcon, Group as GroupIcon, Security } from '@mui/icons-material';
import { groupService, Group } from '../services/groupService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getApiCollection, getProp } from '../utils/apiResponse';
import { styles } from '../utils/styles';

type GroupFilter = 'all' | 'security' | 'microsoft365';

export const Groups: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<GroupFilter>(
    (searchParams.get('filter') as GroupFilter) || 'all'
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const navigate = useNavigate();

  useEffect(() => {
    const filterParam = searchParams.get('filter') as GroupFilter;
    if (filterParam && ['all', 'security', 'microsoft365'].includes(filterParam)) {
      setFilter(filterParam);
    }
  }, [searchParams]);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await groupService.getGroups({ top: 999 });
      const groups = getApiCollection<Group>(response, 'loading groups');
      setGroups(groups);
    } catch (err: any) {
      setError(err.message || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const getFilterCounts = useMemo(() => {
    const security = groups.filter((group: any) => {
      const groupTypes = getProp(group, 'groupTypes') || [];
      return getProp(group, 'securityEnabled') && !groupTypes.includes('Unified');
    }).length;
    const microsoft365 = groups.filter((group: any) => {
      const groupTypes = getProp(group, 'groupTypes') || [];
      return groupTypes.includes('Unified');
    }).length;

    return { security, microsoft365 };
  }, [groups]);

  const filteredGroups = useMemo(() => {
    if (filter === 'all') return groups;

    return groups.filter((group: any) => {
      const groupTypes = getProp(group, 'groupTypes') || [];

      switch (filter) {
        case 'security':
          return getProp(group, 'securityEnabled') && !groupTypes.includes('Unified');
        case 'microsoft365':
          return groupTypes.includes('Unified');
        default:
          return true;
      }
    });
  }, [groups, filter]);

  const paginatedGroups = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredGroups.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredGroups, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={styles.pageHeader}>
        <Typography variant="h4">Groups</Typography>
        <Button variant="contained" startIcon={<AddIcon />}>
          Create Group
        </Button>
      </Box>

      <Box sx={styles.filterSection}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, newFilter) => newFilter && setFilter(newFilter)}
          aria-label="group filter"
        >
          <ToggleButton value="all" aria-label="all groups">
            <GroupIcon sx={styles.iconMargin} />
            All ({groups.length})
          </ToggleButton>
          <ToggleButton value="security" aria-label="security groups">
            <Security sx={styles.iconMargin} />
            Security Groups ({getFilterCounts.security})
          </ToggleButton>
          <ToggleButton value="microsoft365" aria-label="microsoft 365 groups">
            <GroupIcon sx={styles.iconMargin} />
            Microsoft 365 Groups ({getFilterCounts.microsoft365})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={styles.errorAlert}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={styles.tableHead}>
            <TableRow>
              <TableCell sx={styles.tableHeaderCell}>Display Name</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Description</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Type</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Email</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedGroups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No groups matched the current filter. If you expected results, verify the filter
                  or API permissions.
                </TableCell>
              </TableRow>
            ) : (
              paginatedGroups.map((group: any) => {
                const groupTypes = getProp(group, 'groupTypes') || [];
                const groupId = getProp(group, 'id');

                return (
                  <TableRow
                    key={groupId}
                    hover
                    sx={styles.tableRow}
                    onClick={() => navigate(`/groups/${groupId}`)}
                  >
                    <TableCell>{getProp(group, 'displayName')}</TableCell>
                    <TableCell>{getProp(group, 'description') || '-'}</TableCell>
                    <TableCell>
                      {groupTypes.includes('Unified') ? (
                        <Chip label="Microsoft 365" size="small" color="primary" />
                      ) : getProp(group, 'securityEnabled') ? (
                        <Chip label="Security" size="small" color="default" />
                      ) : (
                        <Chip label="Distribution" size="small" color="secondary" />
                      )}
                    </TableCell>
                    <TableCell>{getProp(group, 'mail') || '-'}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredGroups.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};
